import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './apiClient';

const externalOrderKeys = {
  all: ['externalOrders'],
  lists: () => [...externalOrderKeys.all, 'list'],
  list: (filters) => [...externalOrderKeys.lists(), filters || {}],
  details: () => [...externalOrderKeys.all, 'detail'],
  detail: (id) => [...externalOrderKeys.details(), id],
};

const buildListParams = (filtersOrStatus) => {
  const filters =
    typeof filtersOrStatus === 'string'
      ? { status: filtersOrStatus }
      : filtersOrStatus || {};
  const params = {};

  if (filters.status) params.status = filters.status;
  if (filters.search) params.search = filters.search;
  if (filters.page) params.page = filters.page;
  if (filters.per_page) params.per_page = filters.per_page;

  return params;
};

export const useExternalOrders = (filtersOrStatus, options = {}) => {
  const params = buildListParams(filtersOrStatus);

  return useQuery({
    queryKey: externalOrderKeys.list(params),
    queryFn: async () => {
      const response = await apiClient.get('external_orders', { params });
      return response.data;
    },
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    ...options,
  });
};

export const useExternalOrder = (id, options = {}) => {
  return useQuery({
    queryKey: externalOrderKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get(`external_orders/${id}`);
      return response.data;
    },
    enabled: !!id,
    staleTime: 30 * 1000,
    ...options,
  });
};

export const useCreateExternalOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (orderData) => {
      const response = await apiClient.post('external_orders', orderData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: externalOrderKeys.lists() });
    },
    onError: (error) => {
      console.error('Error creating external order:', error);
    },
  });
};

export const useCheckExternalOrderStock = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.post(`external_orders/${id}/check-stock`);
      return response.data;
    },
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: externalOrderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: externalOrderKeys.detail(id) });
    },
    onError: (error) => {
      console.error('Error checking external order stock:', error);
    },
  });
};

export const useAuthorizeExternalOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.post(`external_orders/${id}/authorize`);
      return response.data;
    },
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: externalOrderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: externalOrderKeys.detail(id) });
    },
    onError: (error) => {
      console.error('Error authorizing external order:', error);
    },
  });
};

export const useSetExternalOrderConsumptionAuthorization = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, consumption_authorized = true }) => {
      const response = await apiClient.put(
        `external_orders/${id}/consumption-authorization`,
        { consumption_authorized }
      );
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: externalOrderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: externalOrderKeys.detail(variables.id) });
    },
    onError: (error) => {
      console.error('Error setting external order consumption authorization:', error);
    },
  });
};

export const useProcessExternalOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.post(`external_orders/${id}/process`);
      return response.data;
    },
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: externalOrderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: externalOrderKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: ['stockTransfers'] });
    },
    onError: (error) => {
      console.error('Error processing external order:', error);
    },
  });
};
